import { useState, useRef, useEffect } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { cldImageSrcSet, cldLQIP, cldImage } from '@/utils/media';
import { POSTER_FALLBACK_PATH } from '@/utils/fallbacks';

interface OptimizedImageProps {
  publicId: string;
  alt: string;
  className?: string;
  sizes?: string; // CSS sizes attribute for responsive images
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
  aspectRatio?: string; // e.g., "16/9", "1/1", "4/3"
}

export const OptimizedImage = ({ 
  publicId,
  alt,
  className,
  sizes = "(max-width: 640px) 92vw, 600px", // Default responsive sizes
  onClick,
  onLoad,
  onError,
  aspectRatio = "16/9"
}: OptimizedImageProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [lqipLoaded, setLqipLoaded] = useState(false);
  const mode = useConnectionMode();

  // Generate responsive srcset and LQIP
  const avifSrcSet = cldImageSrcSet(publicId, mode, 'avif');
  const webpSrcSet = cldImageSrcSet(publicId, mode, 'webp');
  const fallbackUrl = cldImage(publicId, mode);
  const lqipUrl = cldLQIP(publicId);

  // Preload LQIP for blur-up effect
  useEffect(() => {
    const img = new Image();
    img.onload = () => setLqipLoaded(true);
    img.src = lqipUrl;
  }, [lqipUrl]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleLoad = () => {
      setIsLoading(false);
      onLoad?.();
    };

    const handleError = () => {
      console.error('Image failed to load:', fallbackUrl);
      setHasError(true);
      setIsLoading(false);
      onError?.();
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [fallbackUrl, onLoad, onError]);

  if (hasError) {
    return (
      <img 
        src={POSTER_FALLBACK_PATH}
        alt={alt}
        className={className}
        style={{ aspectRatio }}
        loading="lazy"
      />
    );
  }

  return (
    <div className="relative">
      {/* LQIP blur-up background */}
      {lqipLoaded && isLoading && (
        <img
          src={lqipUrl}
          alt=""
          className={`${className} absolute inset-0 object-cover filter blur-sm scale-110 transition-opacity duration-300`}
          style={{ aspectRatio }}
          aria-hidden="true"
        />
      )}
      
      <picture>
        <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
        <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
        <img 
          ref={imgRef}
          src={fallbackUrl}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          style={{ aspectRatio }}
          sizes={sizes}
          onClick={onClick}
          loading="lazy"
          decoding="async"
        />
      </picture>
      
      {/* Loading state without LQIP fallback */}
      {isLoading && !lqipLoaded && (
        <div className={`${className} absolute inset-0 bg-muted animate-pulse flex items-center justify-center`} style={{ aspectRatio }}>
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};